import { VolumeSpec } from "../volumeSpec";
import { DefaultOptions, LocalOptions } from "./workloadOptions";
import { Base } from "../base";
import { EnvVar } from "../env";
import { Sidecar } from "../sidecar/entity";

export type HealthCheckSpecHttpGetScheme = "HTTP" | "HTTPS";
export interface HealthCheckSpecHttpGet {
  path: string;
  port?: number;
  httpHeaders?: {
    name: string;
    value: string;
  }[];
  scheme: HealthCheckSpecHttpGetScheme;
}

export interface HealthCheckSpec {
  exec?: {
    command: string[];
  };
  grpc?: {
    port: number;
  };
  tcpSocket?: {
    port: number;
  };
  httpGet?: HealthCheckSpecHttpGet;
  initialDelaySeconds: number;
  periodSeconds: number;
  timeoutSeconds: number;
  successThreshold: number;
  failureThreshold: number;
}

export type ScalingPolicy = "OrderedReady" | "Parallel";
export interface RolloutOptions {
  minReadySeconds: number;
  maxSurgeReplicas?: string;
  scalingPolicy: ScalingPolicy;
  terminationGracePeriodSeconds: number;
  maxUnavailableReplicas?: string;
}
export interface SecurityOptions {
  filesystemGroupId?: number;
  runAsUser?: number;
}
export interface GpuResource {
  nvidia?: {
    model: any;
    quantity: number;
  };
  custom?: {
    resource: string;
    runtimeClass?: string;
    quantity?: number;
  };
}

export type PortProtocol = "http" | "http2" | "grpc" | "tcp";

export interface Port {
  protocol: PortProtocol;
  number: number;
}

export interface ContainerSpec {
  name: string;
  image: string;
  workingDir?: string;
  metrics?: {
    port: number;
    path: string;
    dropMetrics?: string[];
  };
  port?: number;
  ports?: Port[];
  memory: string;
  readinessProbe?: HealthCheckSpec;
  livenessProbe?: HealthCheckSpec;
  cpu: string;
  minCpu?: string;
  minMemory?: string;
  env?: EnvVar[];
  gpu?: GpuResource;
  inheritEnv: boolean;
  command?: string;
  args?: string[];
  lifecycle?: {
    postStart?: {
      exec?: {
        command?: string[];
      };
    };
    preStop?: {
      exec?: {
        command?: string[];
      };
    };
  };
  volumes?: VolumeSpec[];
}
export interface HealthCheckStatus {
  active: boolean;
  success?: boolean;
  code?: number;
  message?: string;
  failures?: number;
  successes?: number;
  lastChecked?: Date;
}
export interface ResolvedImage {
  digest?: string;
  manifests?: {
    image: string;
    mediaType: string;
    digest: string;
    platform?: {
      [x: string]: string;
    };
  }[];
}
export interface ResolvedImages {
  resolvedForVersion?: number;
  resolvedAt?: Date;
  errorMessages?: string[];
  images?: ResolvedImage[];
}
export interface WorkloadStatus {
  parentId?: string;
  canonicalEndpoint?: string;
  endpoint?: string;
  internalName?: string;
  replicaInternalNames?: string[];
  healthCheck?: HealthCheckStatus;
  currentReplicaCount?: number;
  resolvedImages?: ResolvedImages;
  loadBalancer: {
    origin: string;
    url: string;
  }[];
  suspendedStatus?: "suspended" | "notSuspended" | "partiallySuspended";
}

export type OutboundAllowPortProtocol = "http" | "https" | "tcp";
export type InternalInboundAllowType = "none" | "same-gvc" | "same-org" | "workload-list";

export interface FirewallSpec {
  external?: {
    inboundAllowCIDR?: string[];
    inboundBlockedCIDR?: string[];
    outboundAllowHostname?: string[];
    outboundAllowPort?: {
      protocol: OutboundAllowPortProtocol;
      number: number;
    }[];
    outboundAllowCIDR?: string[];
    outboundBlockedCIDR?: string[];
    http?: {
      inboundHeaderFilter: {
        key: string;
        allowedValues?: string[];
        blockedValues?: string[];
      }[];
    };
  };
  internal?: {
    inboundAllowType?: InternalInboundAllowType;
    inboundAllowWorkload?: string[];
  };
}
export type ScheduleType = string;
export type ConcurrencyPolicy = "Forbid" | "Replace" | "Allow";
export type RestartPolicy = "OnFailure" | "Never";

export interface JobSpec {
  schedule?: ScheduleType;
  concurrencyPolicy: ConcurrencyPolicy;
  historyLimit: number;
  restartPolicy?: RestartPolicy;
  activeDeadlineSeconds?: number;
}

export interface LoadBalancerPort {
  externalPort: number;
  protocol: string;
  containerPort?: number;
  scheme?: string;
}

export interface LoadBalancer {
  direct?: {
    enabled: boolean;
    ports?: LoadBalancerPort[];
    ipSet?: string;
  };
  geoLocation?: {
    enabled?: boolean;
    headers?: {
      asn?: string;
      city?: string;
      country?: string;
      region?: string;
    };
  };
  replicaDirect?: boolean;
}

export interface Extras {
  // Extra kubernetes modifications. Only used for BYOK.`
  affinity?: any;
  tolerations?: any[];
  topologySpreadConstraints?: any[];
}

export interface RequestRetryPolicy {
  attempts: number;
  retryOn: string[];
}

export type WorkloadType = "serverless" | "standard" | "cron" | "stateful";

export interface WorkloadSpec {
  type: WorkloadType;
  identityLink?: string;
  containers: ContainerSpec[];
  firewallConfig?: FirewallSpec;
  defaultOptions?: DefaultOptions;
  localOptions?: LocalOptions;
  job?: JobSpec;
  sidecar?: Sidecar;
  supportDynamicTags: boolean;
  rolloutOptions?: RolloutOptions;
  securityOptions?: SecurityOptions;
  loadBalancer?: LoadBalancer;
  extras?: Extras;
  requestRetryPolicy?: RequestRetryPolicy;
}
export interface Workload extends Base {
  name: string;
  gvc?: string;
  spec: WorkloadSpec;
  status?: WorkloadStatus;
  health?: {
    readiness: string;
    syncFailed: boolean;
    totalReplicas?: number;
    readyReplicas?: number;
    totalLocations?: number;
    readyLocations?: number;
  };
}

export const firewallRequirementsForVolumes = {
  "s3://": ["*.amazonaws.com"],
  "gs://": ["*.googleapis.com"],
  "azureblob://": ["*.blob.core.windows.net", "*.azure.com"],
  "azurefs://": ["*.file.core.windows.net", "*.azure.com"],
};

export const envCplnReferenceOptions = [
  "metadata.name",
  "metadata.namespace",
  "spec.nodeName",
  "status.hostIP",
  "status.podIP",
  "status.podIPs",
];
